import React, { useCallback, useEffect, useState } from "react";
import { Upload, Trash2, Download, X, FileStack } from "lucide-react";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const DOC_TYPES = ["Invoice", "Credit Note", "Debit Note", "Delivery Challan", "E-Way Bill", "Receipt"];
const STATUSES = ["Open", "Paid", "Partially Paid", "Cancelled", "Overdue"];
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y,m,d]=String(iso).split("-"); return y&&m&&d?`${d}-${m}-${y}`:iso; };

const EMPTY = {
  customer_id: "", doc_type: "Invoice", doc_number: "", doc_date: "",
  po_number: "", amount: 0, status: "Open", remarks: "",
};

export function AdminSalesDocuments({ token, onUnauthorized }) {
  const [docs, setDocs] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState({ customer_id: "" });

  const load = useCallback(async () => {
    const [dr, cr] = await Promise.all([
      fetch(`${API_BASE}/api/admin/sales-documents${filter.customer_id ? `?customer_id=${filter.customer_id}` : ""}`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API_BASE}/api/admin/customers`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (dr.status === 401 || cr.status === 401) return onUnauthorized?.();
    setDocs(await dr.json());
    setCustomers(await cr.json());
  }, [token, filter, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return alert("Please pick a PDF file.");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(uploading).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      const r = await fetch(`${API_BASE}/api/admin/sales-documents`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Upload failed");
      setUploading(null); setFile(null); load();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!confirm(`Delete ${d.doc_type} ${d.doc_number}?`)) return;
    const r = await fetch(`${API_BASE}/api/admin/sales-documents/${d.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return alert("Delete failed");
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-sales-docs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-[#0a2a5e] flex items-center gap-2"><FileStack className="h-5 w-5" /> Sales Documents</h2>
          <p className="text-xs text-slate-500">Upload invoices, credit / debit notes, delivery challans, e-way bills and receipts. Customers can view these in their portal.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter.customer_id} onChange={(e) => setFilter({ customer_id: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.code ? `(${c.code})` : ""}</option>)}
          </select>
          <Button onClick={() => setUploading({ ...EMPTY, doc_date: new Date().toISOString().slice(0,10) })} disabled={customers.length === 0}
            className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white" data-testid="upload-sales-doc-btn">
            <Upload className="h-4 w-4 mr-1" /> Upload Document
          </Button>
        </div>
      </div>

      {customers.length === 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">Add customers first (Customers tab) before uploading documents.</div>}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Customer</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Doc No</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">PO</th>
              <th className="px-4 py-3 font-semibold text-right">Amount</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!docs && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {docs && docs.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No documents uploaded yet.</td></tr>}
            {docs?.map((d) => (
              <tr key={d.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#0a2a5e]">{d.customer_name}</div>
                  {d.customer_code && <div className="text-xs text-slate-400 font-mono">{d.customer_code}</div>}
                </td>
                <td className="px-4 py-3"><span className="inline-block px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold">{d.doc_type}</span></td>
                <td className="px-4 py-3 font-semibold">{d.doc_number}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(d.doc_date)}</td>
                <td className="px-4 py-3 font-mono text-xs">{d.po_number || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">₹ {inr(d.amount)}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">{d.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => remove(d)} className="text-red-500 hover:text-red-700 p-1.5" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {uploading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setUploading(null)}>
          <form onSubmit={upload} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#0a2a5e]">Upload Sales Document</h3>
              <button type="button" onClick={() => setUploading(null)} className="text-slate-400 hover:text-slate-700 p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Customer *</label>
                <select value={uploading.customer_id} onChange={(e) => setUploading({ ...uploading, customer_id: e.target.value })} required
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                  <option value="">— Select customer —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.code ? `(${c.code})` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Document Type *</label>
                <select value={uploading.doc_type} onChange={(e) => setUploading({ ...uploading, doc_type: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                  {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                <select value={uploading.status} onChange={(e) => setUploading({ ...uploading, status: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
                  {STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Document Number *</label>
                <input type="text" required value={uploading.doc_number} onChange={(e) => setUploading({ ...uploading, doc_number: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Document Date *</label>
                <input type="date" required value={uploading.doc_date} onChange={(e) => setUploading({ ...uploading, doc_date: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">PO Number</label>
                <input type="text" value={uploading.po_number} onChange={(e) => setUploading({ ...uploading, po_number: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Amount (₹)</label>
                <input type="number" step="0.01" value={uploading.amount} onChange={(e) => setUploading({ ...uploading, amount: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Remarks (optional)</label>
                <input type="text" value={uploading.remarks} onChange={(e) => setUploading({ ...uploading, remarks: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">PDF File *</label>
                <input type="file" accept="application/pdf,.pdf" required onChange={(e) => setFile(e.target.files?.[0])}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white" />
              </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
              <Button type="button" variant="outline" onClick={() => setUploading(null)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white" data-testid="save-upload-btn">
                {saving ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
